import React, { useState, useEffect } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Box, Typography, Card, Grid, Chip, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Tabs, Tab, Tooltip
} from '@mui/material';
import InboxIcon from '@mui/icons-material/Inbox';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EmailIcon from '@mui/icons-material/Email';
import ReceiptIcon from '@mui/icons-material/Receipt';
import Layout from '../components/Layout';
import ExpandableTable from '../components/ExpandableTable';
import api from '../services/api';

const CATEGORIES = ['invoice', 'plans', 'permit', 'contract', 'inspection', 'insurance', 'general', 'other'];

function guessCategory(item) {
  const s = (
    (item.subject || '') + ' ' +
    (item.attachment_name || '') + ' ' +
    (item.body_text || '')
  ).toLowerCase();
  if (/(invoice|inv-\d|\bbill\b|\bstatement\b|payment\s+due|amount\s+due|remit)/.test(s)) return 'invoice';
  if (/permit/.test(s)) return 'permit';
  if (/(plan|drawing|blueprint|cad)/.test(s)) return 'plans';
  if (/(contract|agreement|sow)/.test(s)) return 'contract';
  if (/insp/.test(s)) return 'inspection';
  if (/insur/.test(s)) return 'insurance';
  return 'general';
}

function fileEmoji(name) {
  const ext = (name || '').split('.').pop().toLowerCase();
  if (ext === 'pdf') return '📄';
  if (['jpg','jpeg','png','gif','heic','webp'].includes(ext)) return '🖼️';
  if (['doc','docx'].includes(ext)) return '📝';
  if (['xls','xlsx','csv'].includes(ext)) return '📊';
  return '📁';
}

export default function Inbox() {
  const [tab, setTab] = useState(0);
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignDialog, setAssignDialog] = useState({ open: false, item: null });
  const [assignForm, setAssignForm] = useState({ project_id: '', category: 'general', name: '', description: '' });
  const [invoiceDialog, setInvoiceDialog] = useState({ open: false, item: null });
  const [invoiceForm, setInvoiceForm] = useState({ contract_id: '',
    project_id: '', vendor_id: '', amount: '', invoice_number: '', invoice_date: '', notes: ''
  });
  const [vendors, setVendors] = useState([]);
  const [vendorHint, setVendorHint] = useState({ name: '', phone: '', email: '', address: '' });
  const [contracts, setContracts] = useState([]);
  useEffect(() => { api.get('/contracts').then(r => setContracts(Array.isArray(r.data) ? r.data.filter(c => ['active','completed'].includes(c.status)) : [])).catch(() => {}); }, []);
  const [creatingVendor, setCreatingVendor] = useState(false);

  const statusByTab = ['unassigned', 'assigned', 'discarded'];

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/inbound?status=' + statusByTab[tab]);
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      console.error(e);
      setItems([]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  useEffect(() => {
    api.get('/projects')
      .then(r => setProjects(Array.isArray(r.data) ? r.data : []))
      .catch(() => setProjects([]));
    api.get('/vendors')
      .then(r => setVendors(Array.isArray(r.data) ? r.data : []))
      .catch(() => setVendors([]));
  }, []);

  const openAssign = (item) => {
    const cat = guessCategory(item);
    if (cat === 'invoice') {
      openInvoice(item);
      return;
    }
    setAssignForm({
      project_id: '',
      category: cat,
      name: item.attachment_name || '',
      description: item.subject ? 'From email: ' + item.subject : ''
    });
    setAssignDialog({ open: true, item });
  };

  const openInvoice = async (item) => {
    // Prefer Document AI extracted data when available
    const ed = item.extracted_data && typeof item.extracted_data === 'object' && !item.extracted_data.error
      ? item.extracted_data : null;

    // Fallback: parse from subject/filename
    const text = ((item.subject || '') + ' ' + (item.attachment_name || ''));
    const m = text.match(/(INV[-_ ]?\d+|invoice[\s#:-]*(\d+))/i);
    const guessedNum = m ? (m[1] || ('INV-' + m[2])).replace(/\s+/g, '-').toUpperCase() : '';

    // Fallback: amount from body
    let guessedAmount = '';
    if (item.body_text) {
      const am = item.body_text.match(/\$\s*([0-9]{1,3}(?:,[0-9]{3})*(?:\.[0-9]{2})?|[0-9]+(?:\.[0-9]{2})?)/);
      if (am) guessedAmount = am[1].replace(/,/g, '');
    }

    // Match vendor (email -> exact name -> partial name)
    let matchedVendorId = '';
    let hint = { name: '', phone: '', email: '', address: '' };
    try {
      const r = await api.get('/inbound/' + item.id + '/match-vendor');
      if (r.data && r.data.vendor) {
        matchedVendorId = r.data.vendor.id;
      } else if (r.data && r.data.extracted_vendor_name) {
        const ed2 = item.extracted_data || {};
        hint = {
          name: r.data.extracted_vendor_name,
          phone: ed2.vendor_phone || '',
          email: ed2.vendor_email || '',
          address: ed2.vendor_address || ''
        };
      }
    } catch (e) { /* non-fatal */ }
    setVendorHint(hint);

    // Build form values, preferring extracted data
    const finalAmount = ed && ed.total_amount != null ? String(ed.total_amount) : guessedAmount;
    const finalNumber = ed && ed.invoice_number ? ed.invoice_number : guessedNum;
    const finalDate = ed && ed.invoice_date ? ed.invoice_date : new Date().toISOString().slice(0,10);

    let notes = item.subject ? 'From email: ' + item.subject : '';
    if (ed) {
      const extraNotes = [];
      if (ed.vendor_name) extraNotes.push('Vendor: ' + ed.vendor_name);
      if (ed.due_date) extraNotes.push('Due: ' + ed.due_date);
      if (ed.payment_terms) extraNotes.push('Terms: ' + ed.payment_terms);
      if (ed.subtotal != null) extraNotes.push('Subtotal: $' + ed.subtotal);
      if (ed.total_tax != null) extraNotes.push('Tax: $' + ed.total_tax);
      if (Array.isArray(ed.line_items) && ed.line_items.length > 0) {
        extraNotes.push('Line items: ' + ed.line_items.length);
      }
      if (extraNotes.length > 0) notes = notes + (notes ? '\n\n' : '') + extraNotes.join('\n');
    }

    setInvoiceForm({
      project_id: '',
      contract_id: '',
      vendor_id: matchedVendorId,
      amount: finalAmount,
      invoice_number: finalNumber,
      invoice_date: finalDate,
      notes
    });
    setInvoiceDialog({ open: true, item });
  };

  const handleCreateVendor = async () => {
    if (!vendorHint.name) return;
    if (!invoiceForm.project_id) {
      alert('Pick a project first — vendor will be tied to that project.');
      return;
    }
    setCreatingVendor(true);
    try {
      const r = await api.post('/vendors', {
        project_id: invoiceForm.project_id,
        company_name: vendorHint.name,
        contact_name: '',
        email: vendorHint.email || null,
        phone: vendorHint.phone || null,
        address: vendorHint.address || null,
        trade_category: null
      });
      const newV = r.data;
      setVendors(prev => [newV, ...prev.filter(v => v && v.id !== newV.id)]);
      setInvoiceForm(f => ({ ...f, vendor_id: newV.id }));
      setVendorHint({ name: '', phone: '', email: '', address: '' });
    } catch (e) {
      console.error(e);
      alert('Failed to create vendor: ' + (e.response?.data?.error || e.message));
    }
    setCreatingVendor(false);
  };

  const handleReExtract = async (item) => {
    try {
      await api.post('/inbound/' + item.id + '/re-extract');
      alert('Extraction queued. Reload in ~10 seconds to see updated values.');
      setTimeout(() => load(), 10000);
    } catch (e) { console.error(e); }
  };

  const handleCreateInvoice = async () => {
    if (!invoiceForm.project_id || !invoiceForm.amount) return;
    try {
      await api.post('/inbound/' + invoiceDialog.item.id + '/create-invoice', invoiceForm);
      setInvoiceDialog({ open: false, item: null });
      load();
    } catch (e) {
      console.error(e);
      alert('Failed to create invoice: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleAssign = async () => {
    if (!assignForm.project_id) return;
    try {
      await api.post('/inbound/' + assignDialog.item.id + '/assign', assignForm);
      setAssignDialog({ open: false, item: null });
      load();
    } catch (e) {
      console.error(e);
      alert('Failed to assign');
    }
  };

  const handleDiscard = async (item) => {
    if (!window.confirm('Discard this attachment?')) return;
    try {
      await api.post('/inbound/' + item.id + '/discard');
      load();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a237e' }}>Inbox</Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Forwarded email attachments. Forward emails to <b>upload@yaplar.com</b> and triage them here.
          </Typography>
        </Box>

        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Tab label="Unassigned" />
          <Tab label="Assigned" />
          <Tab label="Discarded" />
        </Tabs>

        {loading ? (
          <Typography sx={{ color: '#999' }}>Loading...</Typography>
        ) : items.length === 0 ? (
          <Card elevation={0} sx={{ border: '1px dashed #c5cae9', borderRadius: 3, p: 6, textAlign: 'center' }}>
            <InboxIcon sx={{ fontSize: 64, color: '#e0e0e0', mb: 1 }} />
            <Typography sx={{ color: '#999' }}>
              {tab === 0
                ? 'No unassigned attachments. Forward an email to upload@yaplar.com to add one.'
                : tab === 1
                ? 'No assigned attachments yet.'
                : 'No discarded attachments.'}
            </Typography>
          </Card>
        ) : (
          <ExpandableTable
            rows={items}
            emptyMessage="No attachments in this view."
            columns={[
              {
                key: 'attachment_name',
                label: 'File',
                render: (it) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                    <Box sx={{ fontSize: 24, lineHeight: 1 }}>{fileEmoji(it.attachment_name)}</Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a237e' }} noWrap title={it.attachment_name}>
                        {String(it.attachment_name || '')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#888' }}>
                        {it.attachment_size ? (Number(it.attachment_size) / 1024).toFixed(1) + ' KB' : ''}
                      </Typography>
                    </Box>
                  </Box>
                )
              },
              {
                key: 'from_email',
                label: 'From',
                render: (it) => (
                  <Box sx={{ minWidth: 0 }}>
                    {it.from_name && <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{String(it.from_name)}</Typography>}
                    <Typography variant="caption" sx={{ color: '#888' }} noWrap title={String(it.from_email || '')}>{String(it.from_email || '')}</Typography>
                  </Box>
                )
              },
              {
                key: 'subject',
                label: 'Subject',
                render: (it) => (
                  <Typography variant="caption" sx={{ color: '#555' }} noWrap title={it.subject}>
                    {String(it.subject || '(no subject)')}
                  </Typography>
                )
              },
              {
                key: 'received_at',
                label: 'Received',
                width: 150,
                render: (it) => (
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    {it.received_at ? new Date(it.received_at).toLocaleString() : ''}
                  </Typography>
                )
              },
              {
                key: 'status',
                label: 'Status',
                width: 120,
                render: (it) => {
                  if (it.status === 'assigned') return <Chip label="Assigned" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', height: 22, fontSize: 11 }} />;
                  if (it.status === 'discarded') return <Chip label="Discarded" size="small" sx={{ bgcolor: '#fbe9e7', color: '#bf360c', height: 22, fontSize: 11 }} />;
                  if (it.extraction_status === 'pending') return <Chip label="Reading..." size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100', height: 22, fontSize: 11 }} />;
                  if (it.extraction_status === 'complete' && it.extracted_data && !it.extracted_data.error) return <Chip label="Read" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', height: 22, fontSize: 11 }} />;
                  if (it.extraction_status === 'failed') return <Chip label="Read failed" size="small" sx={{ bgcolor: '#fbe9e7', color: '#bf360c', height: 22, fontSize: 11 }} />;
                  return <Chip label="Unassigned" size="small" sx={{ bgcolor: '#eceff1', color: '#546e7a', height: 22, fontSize: 11 }} />;
                }
              }
            ]}
            renderExpanded={(it) => (
              <Box>
                {/* Detail row */}
                <Box sx={{ display: 'flex', gap: 4, mb: 2, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>Subject</Typography>
                    <Typography variant="body2">{String(it.subject || '(no subject)')}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>From</Typography>
                    <Typography variant="body2">
                      {it.from_name ? String(it.from_name) + ' <' + String(it.from_email || '') + '>' : String(it.from_email || '')}
                    </Typography>
                  </Box>
                  {it.extracted_data && !it.extracted_data.error && (
                    <Box>
                      <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>Extracted</Typography>
                      <Typography variant="body2">
                        {it.extracted_data.total_amount ? '$' + it.extracted_data.total_amount : ''}
                        {it.extracted_data.invoice_number ? ' \u00b7 #' + it.extracted_data.invoice_number : ''}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-start', borderTop: '1px solid #f0f0f0', pt: 1.5, alignItems: 'center' }}>
                  <Button size="small" variant="outlined" startIcon={<VisibilityIcon />}
                    onClick={() => window.open(it.attachment_url, '_blank')}
                    sx={{ borderRadius: 2, fontSize: 11 }}>
                    Preview
                  </Button>
                  {it.status === 'unassigned' && (
                    <>
                      {it.extraction_status === 'failed' && (
                        <Button size="small" variant="text" onClick={() => handleReExtract(it)}
                          sx={{ color: '#bf360c', fontSize: 11 }}>
                          Retry read
                        </Button>
                      )}
                      <Button size="small" variant="outlined" startIcon={<ReceiptIcon />}
                        onClick={() => openInvoice(it)}
                        sx={{ borderColor: '#2e7d32', color: '#2e7d32', borderRadius: 2, fontSize: 11 }}>
                        Invoice
                      </Button>
                      <Button size="small" variant="contained" startIcon={<AssignmentIcon />}
                        onClick={() => openAssign(it)}
                        sx={{ bgcolor: '#1a237e', borderRadius: 2, fontSize: 11 }}>
                        Assign
                      </Button>
                      <Button size="small" variant="outlined" startIcon={<DeleteOutlineIcon />}
                        onClick={() => handleDiscard(it)}
                        sx={{ borderColor: '#e53935', color: '#e53935', borderRadius: 2, fontSize: 11 }}>
                        Discard
                      </Button>
                    </>
                  )}
                </Box>
              </Box>
            )}
          />
        )}
      </Box>

      <Dialog open={assignDialog.open} onClose={() => setAssignDialog({ open: false, item: null })}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#1a237e', borderBottom: '1px solid #e0e0e0' }}>
          Assign to a project
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField select fullWidth label="Project *" value={assignForm.project_id}
              onChange={e => setAssignForm({ ...assignForm, project_id: e.target.value })}>
              {projects.filter(p => p && p.id).map(p => (
                <MenuItem key={p.id} value={p.id}>{String(p.name || p.id)}</MenuItem>
              ))}
            </TextField>
            <TextField select fullWidth label="Category" value={assignForm.category}
              onChange={e => setAssignForm({ ...assignForm, category: e.target.value })}>
              {CATEGORIES.map(c => (
                <MenuItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</MenuItem>
              ))}
            </TextField>
            <TextField fullWidth label="Document name" value={assignForm.name}
              onChange={e => setAssignForm({ ...assignForm, name: e.target.value })} />
            <TextField fullWidth label="Description" multiline rows={2} value={assignForm.description}
              onChange={e => setAssignForm({ ...assignForm, description: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={() => setAssignDialog({ open: false, item: null })}>Cancel</Button>
          <Button variant="contained" disabled={!assignForm.project_id} onClick={handleAssign}
            sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 3 }}>Assign to project</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={invoiceDialog.open} onClose={() => setInvoiceDialog({ open: false, item: null })}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#2e7d32', borderBottom: '1px solid #e0e0e0' }}>
          Create Invoice from email
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ p: 1.5, bgcolor: '#f5f6fa', borderRadius: 2 }}>
              <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>Attachment</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{invoiceDialog.item?.attachment_name}</Typography>
              <Typography variant="caption" sx={{ color: '#999' }}>
                From: {invoiceDialog.item?.from_email}
              </Typography>
            </Box>
            <TextField select fullWidth label="Project *" value={invoiceForm.project_id}
              onChange={e => setInvoiceForm({ ...invoiceForm, project_id: e.target.value })}>
              {projects.filter(p => p && p.id).map(p => (
                <MenuItem key={p.id} value={p.id}>{String(p.name || p.id)}</MenuItem>
              ))}
            </TextField>
            <TextField select fullWidth label="Vendor" value={invoiceForm.vendor_id}
              onChange={e => setInvoiceForm({ ...invoiceForm, vendor_id: e.target.value, contract_id: '' })}
              helperText={invoiceForm.vendor_id ? 'Vendor matched' : 'Pick the vendor (optional)'}>
              <MenuItem value="">(no vendor)</MenuItem>
              {vendors.filter(v => v && v.id).map(v => (
                <MenuItem key={v.id} value={v.id}>{String(v.company_name || v.contact_name || v.email || v.id)}</MenuItem>
              ))}
            </TextField>
            {(() => {
              const matchingContracts = contracts.filter(c =>
                c.project_id === invoiceForm.project_id &&
                (invoiceForm.vendor_id ? c.vendor_id === invoiceForm.vendor_id : true)
              );
              const contractRequired = matchingContracts.length > 0;
              return (
                <Autocomplete
                  options={matchingContracts}
                  getOptionLabel={(c) => [c.contract_number, c.customer_name || c.project_address || c.project_name, c.scope_description].filter(Boolean).join(' · ')}
                  value={matchingContracts.find(c => c.id === invoiceForm.contract_id) || null}
                  onChange={(e, val) => setInvoiceForm(f => ({ ...f, contract_id: val ? val.id : '' }))}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  disabled={!invoiceForm.project_id}
                  renderInput={(params) => (
                    <TextField {...params}
                      label={contractRequired ? 'Contract *' : 'Contract (optional)'}
                      error={contractRequired && !invoiceForm.contract_id}
                      helperText={
                        !invoiceForm.project_id ? 'Select a project first'
                        : contractRequired
                          ? (invoiceForm.contract_id ? 'Invoice will be filed under this contract' : 'Required — select the contract this invoice belongs to')
                          : 'No matching contract for this project/vendor — optional (e.g. permits, one-off suppliers)'
                      } />
                  )}
                />
              );
            })()}
            {vendorHint.name && !invoiceForm.vendor_id && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, bgcolor: '#fff8e1', border: '1px solid #ffe082', borderRadius: 2 }}>
                <Typography variant="body2" sx={{ flex: 1, color: '#5d4037' }}>
                  Invoice says: <b>{vendorHint.name}</b>
                  {(vendorHint.phone || vendorHint.email) && (
                    <Typography component="span" variant="caption" sx={{ display: 'block', color: '#795548' }}>
                      {[vendorHint.phone, vendorHint.email].filter(Boolean).join(' · ')}
                    </Typography>
                  )}
                </Typography>
                <Button size="small" variant="outlined"
                  disabled={creatingVendor || !invoiceForm.project_id}
                  title={!invoiceForm.project_id ? 'Pick a project first' : ''}
                  onClick={handleCreateVendor}
                  sx={{ color: '#5d4037', borderColor: '#bcaaa4', fontSize: 11 }}>
                  {creatingVendor ? 'Creating...' : (!invoiceForm.project_id ? 'Pick project first' : 'Create vendor')}
                </Button>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField fullWidth required label="Amount ($) *" type="number" inputProps={{ step: '0.01' }}
                value={invoiceForm.amount}
                onChange={e => setInvoiceForm({ ...invoiceForm, amount: e.target.value })} />
              <TextField fullWidth label="Invoice #" value={invoiceForm.invoice_number}
                onChange={e => setInvoiceForm({ ...invoiceForm, invoice_number: e.target.value })} />
            </Box>
            <TextField fullWidth label="Invoice date" type="date" InputLabelProps={{ shrink: true }}
              value={invoiceForm.invoice_date}
              onChange={e => setInvoiceForm({ ...invoiceForm, invoice_date: e.target.value })} />
            <TextField fullWidth label="Notes" multiline rows={2} value={invoiceForm.notes}
              onChange={e => setInvoiceForm({ ...invoiceForm, notes: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={() => setInvoiceDialog({ open: false, item: null })}>Cancel</Button>
          <Button variant="contained"
            disabled={!invoiceForm.project_id || !invoiceForm.amount || (
              contracts.some(c => c.project_id === invoiceForm.project_id && (invoiceForm.vendor_id ? c.vendor_id === invoiceForm.vendor_id : true))
              && !invoiceForm.contract_id
            )}
            onClick={handleCreateInvoice}
            sx={{ bgcolor: '#2e7d32', borderRadius: 2, px: 3 }}>
            Create Invoice
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
